import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';

interface QRScannerProps {
  onScan: (result: string) => void;
  onError?: (error: string) => void;
  active?: boolean;
}

const QRScanner: React.FC<QRScannerProps> = ({ onScan, onError, active = true }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReader = useRef<BrowserMultiFormatReader | null>(null);
  const lastScanTime = useRef<number>(0);
  const [isScanning, setIsScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  useEffect(() => {
    if (active && !isScanning) {
      startScanning();
    } else if (!active && isScanning) {
      pauseScanning(); // إيقاف المسح مؤقتاً بدلاً من إغلاق الكاميرا
    }
    
    return () => {
      stopScanning();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const startScanning = async () => {
    if (!videoRef.current || isScanning) return;

    try {
      // Request camera permission
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      setHasPermission(true);
      
      // Initialize code reader
      codeReader.current = new BrowserMultiFormatReader();
      
      // Start scanning
      codeReader.current.decodeFromVideoDevice(null, videoRef.current, (result, error) => {
        if (result) {
          const currentTime = Date.now();
          const text = result.getText();
          
          // منع المسح المتكرر خلال ثانيتين
          if (currentTime - lastScanTime.current < 2000) {
            console.log('Ignoring duplicate scan within 2 seconds');
            return;
          }
          
          lastScanTime.current = currentTime;
          console.log('QR Code scanned:', text);
          onScan(text);
        }
        
        if (error && onError) {
          // Only log errors, don't stop scanning
          console.log('Scan error (continuing):', error);
        }
      });
      
      setIsScanning(true);
      
    } catch (error: any) {
      console.error('Camera access error:', error);
      setHasPermission(false);
      
      if (onError) {
        onError('فشل في الوصول للكاميرا. تأكد من منح الإذن للكاميرا.');
      }
    }
  };

  const pauseScanning = () => {
    try {
      // إيقاف المسح فقط بدون إغلاق الكاميرا
      if (codeReader.current) {
        codeReader.current.reset();
        codeReader.current = null;
      }
      
      setIsScanning(false);
    } catch (error) {
      console.error('Error pausing scanner:', error);
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    try {
      if (codeReader.current) {
        codeReader.current.reset();
        codeReader.current = null;
      }
      
      // إيقاف stream الكاميرا
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
      
      setIsScanning(false);
    } catch (error) {
      console.error('Error stopping scanner:', error);
      setIsScanning(false);
    }
  };

  if (hasPermission === false) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        textAlign: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: '12px',
        minHeight: '300px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
        <h3 style={{ marginBottom: '12px' }}>مطلوب إذن الكاميرا</h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          يرجى السماح للموقع بالوصول إلى الكاميرا لمسح رمز QR
        </p>
        <button 
          onClick={startScanning}
          style={{
            padding: '12px 24px',
            backgroundColor: '#3A5F5F',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'relative', 
      width: '100%', 
      maxWidth: '500px',
      margin: '0 auto'
    }}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: 'auto',
          borderRadius: '12px',
          backgroundColor: '#000'
        }}
        playsInline
        muted
      />
      
      {/* Scanning overlay */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '200px',
        height: '200px',
        border: '2px solid #00ff00',
        borderRadius: '12px',
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '-1px',
          left: '-1px',
          right: '-1px',
          bottom: '-1px',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '12px',
          animation: 'pulse 2s infinite'
        }} />
      </div>
      
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '14px'
      }}>
        وجه الكاميرا نحو رمز QR
      </div>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default QRScanner;